import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Wall-clock ↔ instant conversion for the app-wide date-range SELECTION.
 *
 * This is a third, distinct use of timezone, and it is deliberately separate
 * from the other two:
 *  - `org-time.ts` is DISPLAY only (how an instant is rendered).
 *  - `format-datetime.ts` is the WIRE (UTC components of an instant).
 *  - this file is SELECTION: what instant the user meant when they picked
 *    "06:00" for a site in `timeZone`.
 *
 * The range store keeps true UTC instants either way — only their construction
 * moves from the viewer's zone to the location's.
 *
 * All day arithmetic runs on `YYYY-MM-DD` strings in UTC, where days are always
 * exactly 24h, then re-resolves through the zone. Adding hours or days to a
 * zoned instant directly would drift by an hour across a DST boundary.
 *
 * A wall clock that does not exist in `timeZone` (the spring-forward gap) has no
 * exact instant; dayjs resolves it to the adjacent valid one, which is the
 * closest defensible answer.
 */

/** The calendar day (`YYYY-MM-DD`) that `instant` falls on in `timeZone`. */
export const zonedDayString = (instant: Date, timeZone: string): string =>
  dayjs(instant).tz(timeZone).format("YYYY-MM-DD");

/** The wall-clock time (`HH:mm`) that `instant` reads as in `timeZone`. */
export const zonedTimeString = (instant: Date, timeZone: string): string =>
  dayjs(instant).tz(timeZone).format("HH:mm");

/**
 * The instant at which `timeZone`'s clocks read `wallClock` — a dayjs-parseable
 * local-time text such as `2026-07-29 06:00:00` or `2026-07-29T06:00`.
 */
export const instantFromZonedWallClock = (wallClock: string, timeZone: string): Date =>
  dayjs.tz(wallClock, timeZone).toDate();

/**
 * Shift `instant` by whole calendar days, keeping the same wall-clock reading in
 * `timeZone`. This is what a "previous / next day" control means; adding 24h to
 * the instant would land an hour off on a DST-transition day.
 */
export const addZonedDays = (instant: Date, days: number, timeZone: string): Date => {
  const zoned = dayjs(instant).tz(timeZone);
  const day = dayjs.utc(zoned.format("YYYY-MM-DD")).add(days, "day").format("YYYY-MM-DD");
  return instantFromZonedWallClock(`${day} ${zoned.format("HH:mm:ss")}`, timeZone);
};
